<script setup lang="ts">
/**
 * An email draft in the phone chat: a compact mail card (who it's to, the subject, three lines of the body and
 * a 44px "Review & send") instead of the whole inbox composer inline. Tapping it opens the DraftSheet, where the
 * creator edits and sends. The card follows the draft wherever it changes: "Sending…" while the Undo window
 * runs, then Sent (with a success tick), or Discarded.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { ArtifactRef } from '../artifacts'
import { draftPreview, recipientLine } from '../cards'
import { isDraftDiscarded, isDraftSent, useWorkspace } from '../workspace'
import { isDraftSending } from '../draftSending'
import { FRESH_MS, loadDraft, peekDraft } from '../recordCache'
import type { DraftRecord } from './workspace/viewers'
import { haptic } from '@/native/haptics'
import { vPress } from '@/directives/press'
import DraftSheet from './DraftSheet.vue'

const props = defineProps<{ artifact: ArtifactRef }>()

const ws = useWorkspace()
/**
 * The row (whether it was sent, the creator's edits; all there is for a draft the agent only opened) is usually
 * warmed with the conversation, so the card paints whole. recordCache drops a cached row a later change rewrote.
 */
const warm = peekDraft(props.artifact.id)
const row = ref<DraftRecord | null>(warm?.value ?? null)
const gone = ref(warm ? !warm.value : false)
const loading = ref(!warm)
const open = ref(false)

async function load(maxAge = 0) {
    try {
        const r = await loadDraft(props.artifact.id, maxAge)
        row.value = r
        gone.value = !r
    } catch {
        if (loading.value) row.value = null
    } finally {
        loading.value = false
    }
}
onMounted(() => load(warm ? FRESH_MS : 0))
watch(() => props.artifact.id, () => load())
// Edits made in the sheet autosave to the row: show them once it closes.
watch(open, (o) => { if (!o) void load() })

const data = computed(() => ({ ...(props.artifact.data ?? {}), ...(row.value ?? {}) }) as Partial<DraftRecord> & Record<string, any>)
const status = computed<'ready' | 'sending' | 'sent' | 'discarded'>(() => {
    const id = props.artifact.id
    // Sent first: a sent draft's row may be gone afterwards.
    if (isDraftSent(id) || data.value.sent_by) return 'sent'
    if (gone.value || isDraftDiscarded(id)) return 'discarded'
    if (isDraftSending(id)) return 'sending'
    return 'ready'
})
watch(status, (now, before) => {
    if (now === 'sent' && before === 'sending') haptic('success')
})
const to = computed(() => recipientLine(data.value.to))
const subject = computed(() => String(data.value.subject || props.artifact.title || 'Email draft'))
const preview = computed(() => draftPreview(data.value.body as string | undefined))

// The card stands in for the draft in the conversation (the invoice view hides its own Send email then).
let untrack: (() => void) | null = null
onMounted(() => { if (ws) untrack = ws.trackChatDraft(props.artifact.id) })
onBeforeUnmount(() => untrack?.())

function review() {
    if (status.value === 'ready') open.value = true
}
</script>

<template>
    <div class="dc-wrap" data-testid="draft-card" :data-status="status">
        <div
            v-press="{ variant: 'card', disabled: status !== 'ready' }"
            class="dc"
            :class="`is-${status}`"
            :role="status === 'ready' ? 'button' : undefined"
            :tabindex="status === 'ready' ? 0 : undefined"
            :aria-label="status === 'ready' ? `Email draft to ${to || 'no one yet'}: ${subject}. Review and send` : undefined"
            @click="review"
            @keydown.enter.self.prevent="review"
        >
            <header class="dc-head">
                <span class="dc-tile" aria-hidden="true"><i class="fa-regular fa-pen-line" /></span>
                <span class="dc-kind">Email draft</span>
                <Transition name="agent-swap" mode="out-in">
                    <span :key="status" class="dc-state" :class="`is-${status}`">
                        <span v-if="status === 'sending'" class="dc-spin" aria-hidden="true" />
                        <svg v-else-if="status === 'sent'" viewBox="0 0 16 16" aria-hidden="true"><path d="M4.2 8.4l2.4 2.3 5.2-5.4" /></svg>
                        {{ { ready: 'Ready to send', sending: 'Sending…', sent: 'Sent', discarded: 'Discarded' }[status] }}
                    </span>
                </Transition>
            </header>

            <p v-if="to" class="dc-to"><span>To</span> {{ to }}</p>
            <p class="dc-subject">{{ subject }}</p>
            <!-- The body fades in over its placeholder when the row lands after the card. -->
            <Transition name="agent-swap" mode="out-in">
                <div v-if="loading && !preview" class="dc-skel" aria-hidden="true">
                    <span class="loading-secondary h-3 w-full rounded" />
                    <span class="loading-secondary h-3 w-4/5 rounded" />
                </div>
                <p v-else-if="preview && status !== 'discarded'" class="dc-preview">{{ preview }}</p>
            </Transition>

            <div v-if="status === 'ready'" class="dc-cta" data-testid="draft-review-send">
                Review &amp; send
                <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6 4l4 4-4 4" /></svg>
            </div>
        </div>

        <DraftSheet :open="open" :artifact="artifact" @close="open = false" />
    </div>
</template>

<style scoped>
.dc-wrap { min-width: 0; }
.dc {
    display: flex;
    flex-direction: column;
    padding: 14px 14px 12px;
    border-radius: 22px;
    background-color: rgba(255, 255, 255, 0.82);
    background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.1));
    box-shadow:
        0 0 0 0.5px rgba(36, 36, 36, 0.08),
        0 6px 18px rgba(36, 36, 36, 0.07),
        inset 1px 1px 1px -0.5px rgba(255, 255, 255, 1);
    text-align: left;
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
    outline: none;
}
.dc[role='button'] { cursor: pointer; }
.dc:focus-visible { box-shadow: 0 0 0 2px var(--agent-accent-ring), 0 6px 18px rgba(36, 36, 36, 0.07); }
html.dark .dc {
    background-color: rgba(68, 64, 60, 0.6);
    background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02));
    box-shadow: 0 0 0 0.5px rgba(255, 255, 255, 0.12), 0 6px 18px rgba(0, 0, 0, 0.35), inset 1px 1px 1px -0.5px rgba(255, 255, 255, 0.08);
}

.dc-head { display: flex; align-items: center; gap: 8px; }
.dc-tile {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 9px;
    font-size: 12px;
    color: #e2400f;
    background: rgb(251 76 31 / 0.1);
}
.dc-kind { flex: 1; min-width: 0; font-size: 13px; font-weight: 600; color: var(--agent-text-2); }
.dc-state {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    color: #e2400f;
    background: rgb(251 76 31 / 0.09);
}
html.dark .dc-state { color: #ff8a5c; background: rgb(251 76 31 / 0.16); }
.dc-state.is-sending { color: var(--agent-text-2); background: var(--agent-press); }
.dc-state.is-sent { color: #059669; background: rgb(16 185 129 / 0.12); }
html.dark .dc-state.is-sent { color: #34d399; }
.dc-state.is-discarded { color: var(--agent-text-3); background: var(--agent-press); }
.dc-state svg { width: 13px; height: 13px; fill: none; stroke: currentColor; stroke-width: 2.2; stroke-linecap: round; stroke-linejoin: round; }
.dc-spin {
    width: 11px;
    height: 11px;
    border-radius: 999px;
    border: 1.6px solid rgb(15 15 15 / 0.15);
    border-top-color: var(--agent-text-2);
    animation: dc-spin 0.8s linear infinite;
}
html.dark .dc-spin { border-color: rgb(255 255 255 / 0.2); border-top-color: var(--agent-text-2); }
@keyframes dc-spin { to { transform: rotate(360deg); } }

.dc-to { margin-top: 12px; font-size: 13.5px; color: var(--agent-text-2); overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.dc-to span { color: var(--agent-text-4); }
.dc-subject {
    margin-top: 2px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.35;
    color: var(--agent-text-1);
    overflow-wrap: anywhere;
}
.is-discarded .dc-subject { color: var(--agent-text-3); text-decoration: line-through; text-decoration-color: rgb(15 15 15 / 0.25); }
.dc-preview {
    display: -webkit-box;
    margin-top: 4px;
    overflow: hidden;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    font-size: 14.5px;
    line-height: 1.42;
    color: var(--agent-text-3);
}
.is-sent .dc-preview { -webkit-line-clamp: 1; }
.dc-skel { display: flex; flex-direction: column; gap: 6px; margin-top: 8px; }
.dc-cta {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 44px;
    margin-top: 12px;
    border-radius: 14px;
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(180deg, var(--agent-accent-2) 0%, var(--agent-accent) 100%);
    box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.35), 0 6px 16px -6px rgb(251 76 31 / 0.6);
}
.dc-cta svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
</style>
